import { useEffect } from 'react';
import type { SatelliteIndex } from '@greeniq/shared';
import { INDEX_META } from '../lib/satelliteColors.js';

interface Explainer {
  what: string;
  watchFor: string;
  decision: string;
  caveats?: string;
}

const EXPLAINERS: Record<SatelliteIndex, Explainer> = {
  ndvi: {
    what: 'Classic vigor index from red + NIR. High = dense healthy canopy; low = sparse, stressed, or dormant turf.',
    watchFor: 'Week-over-week drops of 10%+ on greens or tees. NDVI saturates on lush fairways — use GNDVI there.',
    decision: 'Universal baseline. Pair with NDRE for early stress and NDWI for moisture to disambiguate drop causes.',
  },
  ndre: {
    what: 'Red-edge variant. Tracks chlorophyll + nitrogen status. Sensitive 5–10 days before visible NDVI drop.',
    watchFor: 'A 15%+ WoW drop triggers an alert — this is often a nitrogen deficiency, shade stress, or disease precursor.',
    decision: 'Best early-warning signal for disease and fertility programs. If NDRE drops but NDVI holds, dig into recent applications + rootzone data.',
  },
  gndvi: {
    what: 'Green / NIR ratio. More sensitive than NDVI at high biomass — useful on mature fairways and aprons where NDVI saturates.',
    watchFor: 'Sharp drops on healthy-looking turf when NDVI barely moves. Often flags subtle chlorosis.',
    decision: 'Use alongside NDVI on zones with established dense canopy. On sparse rough or sandy bunkers, stick with SAVI / MSAVI.',
    caveats: 'Less validated for turf specifically than NDVI/NDRE — treat as secondary confirmation.',
  },
  savi: {
    what: 'Soil-adjusted NDVI with L = 0.5. Reduces soil reflectance noise on sparse or recently-seeded turf.',
    watchFor: 'SAVI running higher than NDVI = visible soil is influencing NDVI. Useful to rule out false NDVI positives in spring green-up.',
    decision: 'Primary choice right after aeration or on thin rough. MSAVI auto-adjusts and is preferred for daily monitoring.',
    caveats: 'L = 0.5 is the global agricultural default; dense greens technically warrant L = 0.25 — trust MSAVI over SAVI when in doubt.',
  },
  msavi: {
    what: 'Modified SAVI — the L factor self-tunes per pixel. Robust across growth stages without operator choice.',
    watchFor: 'Large MSAVI / NDVI divergence suggests significant soil exposure (bare patches, divots) in the zone mask.',
    decision: 'Safe default for every zone type. No knob-turning required. Prefer over SAVI for routine monitoring.',
  },
  ndwi: {
    what: 'Gao 1996: (NIR − SWIR) / (NIR + SWIR). Measures vegetation water content / canopy moisture, not water bodies.',
    watchFor: 'Sharp drops indicate canopy dry-down. A rising trend after irrigation = water penetrated the canopy successfully.',
    decision: 'Cross-check with soil-moisture sensors when installed. Before sensors exist, NDWI is the fastest proxy for "is irrigation actually reaching the rootzone?"',
    caveats: 'Uses the Gao (SWIR) variant, not the McFeeters (green) variant. McFeeters is for water-body delineation and lands in Phase 11.',
  },
};

interface Props {
  index: SatelliteIndex | null;
  onClose: () => void;
}

export function IndexExplainerDrawer({ index, onClose }: Props) {
  useEffect(() => {
    if (!index) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, onClose]);

  if (!index) return null;
  const meta = INDEX_META[index];
  const info = EXPLAINERS[index];

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={`${meta.label} explainer`}
    >
      <button
        type="button"
        aria-label="Close overlay"
        className="flex-1 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <aside className="w-full sm:w-[420px] max-w-[100vw] h-full bg-bg-card shadow-xl overflow-y-auto flex flex-col">
        <header className="sticky top-0 bg-bg-card border-b border-border px-5 py-4 flex items-center justify-between gap-3">
          <div>
            <p
              className="uppercase tracking-wider text-xs font-semibold"
              style={{ color: meta.color }}
            >
              {meta.tagline}
            </p>
            <h2 className="mt-0.5 text-xl font-display font-bold text-fg">
              {meta.label}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="min-w-[44px] min-h-[44px] px-3 text-fg-muted hover:text-fg"
          >
            ×
          </button>
        </header>

        <div className="px-5 py-5 space-y-5 text-sm">
          <Section title="What it measures" body={info.what} />
          <Section title="What to watch for" body={info.watchFor} />
          <Section title="How to act on it" body={info.decision} />
          {info.caveats && <Section title="Caveats" body={info.caveats} accent="gold" />}
        </div>
      </aside>
    </div>
  );
}

function Section({
  title,
  body,
  accent,
}: {
  title: string;
  body: string;
  accent?: 'gold';
}) {
  const wrapperClass =
    accent === 'gold'
      ? 'p-3 rounded-md bg-greeniq-500/10 text-greeniq-400'
      : undefined;
  return (
    <section className={wrapperClass}>
      <h3 className="text-xs uppercase tracking-wider text-fg-muted font-semibold">
        {title}
      </h3>
      <p className="mt-1 text-fg">{body}</p>
    </section>
  );
}
